import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Cable, Loader2, Pencil, RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const SELECT = 'w-full h-9 px-3 text-sm rounded-md border border-slate-200 bg-white';
const STATUS_STYLE = {
  live: 'bg-emerald-50 text-emerald-700',
  error: 'bg-red-50 text-red-700',
};
const EMPTY = {
  name: '',
  channel: 'pstn',
  hasOutbound: true,
  address: '',
  transport: 'auto',
  auth_username: '',
  password: '',
  hasInbound: false,
  allowed_addresses: '',
};

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join('; ');
  return detail || fallback;
};

function draftFrom(carrier) {
  return {
    name: carrier.name,
    channel: carrier.channel,
    hasOutbound: Boolean(carrier.outbound),
    address: carrier.outbound?.address || '',
    transport: carrier.outbound?.transport || 'auto',
    auth_username: carrier.outbound?.auth_username || '',
    password: '',
    hasInbound: Boolean(carrier.inbound),
    allowed_addresses: (carrier.inbound?.allowed_addresses || []).join('\n'),
  };
}

function payloadFrom(draft) {
  return {
    name: draft.name,
    channel: draft.channel,
    outbound: draft.hasOutbound
      ? {
          address: draft.address,
          transport: draft.transport,
          auth_username: draft.auth_username || null,
          // Blank keeps the stored password.
          ...(draft.password ? { password: draft.password } : {}),
        }
      : null,
    inbound: draft.hasInbound
      ? { allowed_addresses: draft.allowed_addresses.split(/[\s,]+/).filter(Boolean) }
      : null,
  };
}

/**
 * Carrier trunks are the source of truth for LiveKit SIP. sip-control builds one
 * outbound trunk per DID linked here (DID Numbers page) and one inbound trunk per
 * number answered by an agent (Inbound Routes), within ~30s of a save.
 */
export default function CarrierTrunksDashboard() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/api/v1/admin/carrier-trunks`, { headers });
      setCarriers(res.data?.carrier_trunks || []);
    } catch (error) {
      toast.error(errorText(error, 'Failed to load carrier trunks'));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const editing = carriers.find((carrier) => carrier.id === editingId);
  const passwordRequired =
    draft.hasOutbound &&
    draft.auth_username &&
    !draft.password &&
    (!editing?.outbound?.has_password || editing.outbound.auth_username !== draft.auth_username);

  const save = async () => {
    try {
      setBusy('save');
      const body = payloadFrom(draft);
      if (editingId) {
        await axios.put(`${apiUrl}/api/v1/admin/carrier-trunks/${encodeURIComponent(editingId)}`, body, { headers });
      } else {
        await axios.post(`${apiUrl}/api/v1/admin/carrier-trunks`, body, { headers });
      }
      toast.success('Saved; LiveKit is updated within a minute');
      setDraft(EMPTY);
      setEditingId(null);
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Save failed'));
    } finally {
      setBusy('');
    }
  };

  const remove = async (carrier) => {
    if (!window.confirm(`Delete carrier trunk ${carrier.name}? Its password is deleted too.`)) return;
    try {
      setBusy(carrier.id);
      await axios.delete(`${apiUrl}/api/v1/admin/carrier-trunks/${encodeURIComponent(carrier.id)}`, { headers });
      toast.success(`${carrier.name} deleted`);
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Delete failed'));
    } finally {
      setBusy('');
    }
  };

  const startEdit = (carrier) => {
    setEditingId(carrier.id);
    setDraft(draftFrom(carrier));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Cable className="w-6 h-6" /> Carrier Trunks
          </h1>
          <p className="text-sm text-slate-500">
            Define a carrier once; link DID numbers to it and their LiveKit trunks are created and kept in sync.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <section className="p-4 border rounded-xl border-slate-200 bg-white space-y-4">
        <h2 className="font-semibold text-slate-900">{editingId ? `Edit ${editing?.name || ''}` : 'New carrier trunk'}</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="carrier-name">Name</Label>
            <Input id="carrier-name" value={draft.name} placeholder="VoiceLink"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="carrier-channel">Carries</Label>
            <select id="carrier-channel" className={SELECT} value={draft.channel} disabled={Boolean(editingId)}
              onChange={(e) => setDraft({ ...draft, channel: e.target.value })}>
              <option value="pstn">Phone numbers</option>
              <option value="whatsapp">WhatsApp numbers (one per environment)</option>
            </select>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <fieldset className="space-y-3 p-3 rounded-lg border border-slate-100">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <input type="checkbox" checked={draft.hasOutbound}
                onChange={(e) => setDraft({ ...draft, hasOutbound: e.target.checked })} />
              Outbound (we dial the carrier)
            </label>
            {draft.hasOutbound && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1">
                    <Label htmlFor="carrier-address">Address</Label>
                    <Input id="carrier-address" value={draft.address} placeholder="sip.voicelink.co.in:3300"
                      onChange={(e) => setDraft({ ...draft, address: e.target.value.trim() })} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="carrier-transport">Transport</Label>
                    <select id="carrier-transport" className={SELECT} value={draft.transport}
                      onChange={(e) => setDraft({ ...draft, transport: e.target.value })}>
                      {['auto', 'udp', 'tcp', 'tls'].map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="carrier-username">Auth username</Label>
                    <Input id="carrier-username" value={draft.auth_username} placeholder="none"
                      onChange={(e) => setDraft({ ...draft, auth_username: e.target.value.trim() })} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="carrier-password">Password</Label>
                    <Input id="carrier-password" type="password" autoComplete="new-password" value={draft.password}
                      placeholder={editing?.outbound?.has_password ? 'unchanged' : ''}
                      disabled={!draft.auth_username}
                      onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
                  </div>
                </div>
              </>
            )}
          </fieldset>

          <fieldset className="space-y-3 p-3 rounded-lg border border-slate-100">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <input type="checkbox" checked={draft.hasInbound}
                onChange={(e) => setDraft({ ...draft, hasInbound: e.target.checked })} />
              Inbound (the carrier calls us)
            </label>
            {draft.hasInbound && (
              <div className="space-y-1">
                <Label htmlFor="carrier-allowed">Allowed source IPs / CIDRs</Label>
                <textarea id="carrier-allowed" rows={3} className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 font-mono"
                  value={draft.allowed_addresses} placeholder="10.1.0.35/32"
                  onChange={(e) => setDraft({ ...draft, allowed_addresses: e.target.value })} />
                <p className="text-xs text-slate-500">
                  Empty admits any source; only do that where a security group already restricts it.
                </p>
              </div>
            )}
          </fieldset>
        </div>

        <div className="flex gap-2">
          <Button onClick={save}
            disabled={busy === 'save' || !draft.name || (!draft.hasOutbound && !draft.hasInbound)
              || (draft.hasOutbound && !draft.address) || passwordRequired}>
            {busy === 'save' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editingId ? 'Save' : 'Create'}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={() => { setEditingId(null); setDraft(EMPTY); }}>Cancel</Button>
          )}
        </div>
      </section>

      {carriers.map((carrier) => (
        <section key={carrier.id} className="border rounded-xl border-slate-200 bg-white">
          <div className="flex items-start justify-between p-4">
            <div>
              <h3 className="font-semibold text-slate-900">
                {carrier.name}
                <span className="ml-2 text-xs font-normal text-slate-400">{carrier.channel === 'whatsapp' ? 'WhatsApp' : 'Phone'}</span>
              </h3>
              <p className="text-xs text-slate-500 font-mono">
                {carrier.outbound
                  ? `out: ${carrier.outbound.address} ${carrier.outbound.transport.toUpperCase()}${carrier.outbound.auth_username ? ` as ${carrier.outbound.auth_username}` : ''}`
                  : 'no outbound'}
                {' · '}
                {carrier.inbound ? `in: ${carrier.inbound.allowed_addresses.join(', ') || 'any source'}` : 'no inbound'}
              </p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" aria-label={`Edit ${carrier.name}`} onClick={() => startEdit(carrier)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" aria-label={`Delete ${carrier.name}`} onClick={() => remove(carrier)}
                disabled={busy === carrier.id}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {carrier.dids.length > 0 && (
            <table className="w-full text-sm text-left border-t border-slate-100">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2">DID</th>
                  <th className="px-4 py-2">LiveKit trunk</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {carrier.dids.map((did) => (
                  <tr key={did.id}>
                    <td className="px-4 py-2 font-mono">{did.number}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{did.sip_trunk_id || '—'}</td>
                    <td className="px-4 py-2 text-xs max-w-md">
                      <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_STYLE[did.status] || 'bg-amber-50 text-amber-700'}`}>
                        {did.status || 'pending'}
                      </span>
                      {did.detail && <div className="mt-1 text-red-600">{did.detail}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}
      {!loading && carriers.length === 0 && (
        <p className="text-center text-slate-400">No carrier trunks yet.</p>
      )}
    </div>
  );
}
